import * as React from 'react'

import { AdditionalCompensationFormFieldsFragment } from 'generated/resources/graphql'
import { IObservableArray } from 'mobx'

import { observer } from 'mobx-react'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import type Compensation from 'modules/contracts/store/models/Compensation'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type ContractVersionPayrollPolicy from 'modules/contracts/store/models/ContractVersionPayrollPolicy'
import { isVisible } from 'modules/custom_resources/lib/utils'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import type Employee from 'modules/employees/store/models/Employee'
import type JobLevelSalaryRange from 'modules/job_catalog/store/models/JobLevelSalaryRange'

import { LegalEntityContractData } from './legalEntityContractDataContext'

interface Props {
  employee: Employee
  contractVersion: ContractVersion | undefined | null
  salaryRanges: JobLevelSalaryRange[]
  contractVersionPayrollPolicy: ContractVersionPayrollPolicy | null
  legalEntityContractData: LegalEntityContractData
}

export type InMemoryAdditionalCompensation = AdditionalCompensationFormFieldsFragment & {
  tmpID?: string
}

export interface ContractFlow {
  employee: Employee
  contractVersion: ContractVersion | undefined
  customFields: ResourceField[] | null
  customValues: IObservableArray<CustomFieldValue> | null
  compensations: Compensation[]
  additionalCompensations: InMemoryAdditionalCompensation[]
  initialCompensations: Compensation[]
  initialAdditionalCompensations: InMemoryAdditionalCompensation[]
  salaryRanges: JobLevelSalaryRange[]
  contractVersionPolicy: ContractVersionPayrollPolicy | null
  legalEntityContractData: LegalEntityContractData
  setCustomFields: (customFields: ResourceField[]) => void
  setCustomValues: (customFields: IObservableArray<CustomFieldValue>) => void
  setCompensations: (compensations: Compensation[]) => void
  setAdditionalCompensations: (additionalCompensations: InMemoryAdditionalCompensation[]) => void
  setInitialCompensations: (compensations: Compensation[]) => void
  setInitialAdditionalCompensations: (
    additionalCompensations: InMemoryAdditionalCompensation[]
  ) => void
  setContractVersionPolicy: (contractVersionPolicies: ContractVersionPayrollPolicy | null) => void
}

const Context = React.createContext<ContractFlow | null>(null)

export const ContractFlowProvider = observer(
  ({
    employee,
    contractVersion,
    salaryRanges,
    contractVersionPayrollPolicy,
    legalEntityContractData,
    children,
  }: React.PropsWithChildren<Props>) => {
    const [customFields, setCustomFields] = React.useState<ResourceField[] | null>(null)
    const [customValues, setCustomValues] =
      React.useState<IObservableArray<CustomFieldValue> | null>(null)
    const [compensations, setCompensations] = React.useState<Compensation[] | null>(null)
    const [additionalCompensations, setAdditionalCompensations] = React.useState<
      InMemoryAdditionalCompensation[] | null
    >(null)
    const [initialCompensations, setInitialCompensations] = React.useState<Compensation[] | null>(
      null
    )
    const [initialAdditionalCompensations, setInitialAdditionalCompensations] = React.useState<
      InMemoryAdditionalCompensation[] | null
    >(null)
    const [contractVersionPolicy, setContractVersionPolicy] =
      React.useState<ContractVersionPayrollPolicy | null>(contractVersionPayrollPolicy)

    const visibleCustomFields = customFields
      ?.filter((resourceField: ResourceField) =>
        isVisible(employee, contractVersions)(resourceField.attributes.visible)
      )
      .sort((a, b) => a.attributes.position - b.attributes.position)

    const contractFlowData = {
      employee,
      contractVersion,
      customFields: visibleCustomFields,
      customValues,
      compensations,
      initialCompensations,
      initialAdditionalCompensations,
      salaryRanges,
      contractVersionPolicy,
      legalEntityContractData,
      setCustomFields,
      setCustomValues,
      setCompensations,
      setInitialCompensations,
      setInitialAdditionalCompensations,
      additionalCompensations,
      setAdditionalCompensations,
      setContractVersionPolicy,
    } as ContractFlow

    return <Context.Provider value={contractFlowData}>{children}</Context.Provider>
  }
)

export const useContractFlow = (): ContractFlow => {
  const contractFlow = React.useContext(Context)

  if (!contractFlow) {
    throw new Error('useContractFlow must be used within a ContractFlowProvider')
  }

  return contractFlow
}
